import React from 'react';
import Observer, {
  Props as ObserverProps,
} from '@researchgate/react-intersection-observer';
import classnames from 'classnames';

import parseClassnames from '../helpers/parseClassnames';
import getImgxUrl from './getImgxUrl';

import { LazyImageProps } from './types';

const defaultPlaceholder =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAMAAAAFCAIAAAAPE8H1AAAAPUlEQVQIHQEyAM3/Adve7fn/AwD//AHi3ej5BQvm6OcBubnJ/fbqoqSgAbKrsunf1ZKjqwGyl5G5x8qqt7093yDTiccnmAAAAABJRU5ErkJggg==';

/**
 * Requeriments:
 * - It must have a better api
 * - It must use picture, source and image html5 tags
 * - it must lazy load the images
 * - it must handle the dpr x1 and x2
 * - it must add a "progressive animation" (TODO)
 * - it must use auto=compress (this will serve the image in webp if the browser supports it)
 *
 *
 * ==== Animation ====
 * For the animation we need to use the onload event.
 * 1. Show the placeholder
 * 2. Detect the image is in the viewport (using intersection observer) and then start to load the real image.
 * 3. Once the real image is loaded (using the onload event) we change its opacity and transform css properties
 * 4. Hide the placeholder and show the real one.
 *
 * ==== Use will-transform ====
 * We are going to set the will-transform css property dynamically.
 * This property is used to help the browser to optimize the relayout/repaint of the elements.
 * 1. One the image has been "pre-loaded",  set the will-transform property to the picture element
 * 2. After 300ms (when the animation ends), remove the will-transform property
 */

const LazyImage: React.FC<LazyImageProps> = ({
  placeholder,
  src,
  imgxParams,
  observerProps,
  style,
  imgStyle,
  alt,
  className = '',
  imgClassName = '',
  backgroundImage = '',
  pictureStyles = {},
  customOptimizedImage = '',
}) => {
  const imageRef = React.useRef<HTMLImageElement>(null);
  const [isIntersecting, setIsIntersecting] = React.useState(false);
  const [isResourceLoaded, setIsResourceLoaded] = React.useState(false);

  const handleIntersection: ObserverProps['onChange'] = event => {
    if (event.isIntersecting) {
      setIsIntersecting(true);
    }
  };

  const options = {
    onChange: handleIntersection,
    rootMargin: '0px 0px 300px 0px',
    disabled: isResourceLoaded,
    ...observerProps,
  };

  const webpImage = getImgxUrl(src, {
    auto: 'compress',
    fm: 'webp',
    ...imgxParams,
  });

  const placeholderImage = placeholder || defaultPlaceholder;
  const defaultImage = getImgxUrl(src, imgxParams);
  const optimizedImage = `${webpImage}&dpr=1 1x, ${webpImage}&dpr=2 2x`;

  return (
    <Observer {...options}>
      <div
        className={parseClassnames(
          className,
          classnames(`block max-w-full h-auto`, {
            relative: !className.includes('absolute'),
          }),
        )}
        style={style}
      >
        <div
          className="absolute inset-0 z-10"
          style={{
            ...imgStyle,
            opacity: isResourceLoaded ? 0 : 1,
            backgroundImage: backgroundImage || `url(${placeholderImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            transition: 'opacity 0.3s ease-in-out',
            transitionDelay: '100ms',
          }}
        />
        {/*
          We can't not show the image immediately because it will show a broken image while the image resource is loaded
          So, we need to handle it with an onLoad event. Only show the image if the image has already loaded.
        */}
        {isIntersecting && (
          <picture className="absolute inset-0" style={pictureStyles}>
            <source
              srcSet={customOptimizedImage || optimizedImage}
              type="image/webp"
            />
            <img
              ref={imageRef}
              className={parseClassnames(
                imgClassName,
                'w-full h-full block relative object-contain',
              )}
              src={defaultImage}
              alt={alt}
              style={{
                ...imgStyle,
                opacity: isResourceLoaded ? 1 : 0,
                transition: 'opacity 300ms ease-in-out',
              }}
              // this little one is the trick
              onLoad={() => setIsResourceLoaded(true)}
            />
          </picture>
        )}
      </div>
    </Observer>
  );
};

export default LazyImage;
